import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  map,
  prop,
  values as rValues,
  pipe,
  pathOr,
  find,
  propEq,
  pickAll,
  evolve,
  path,
  set,
  propSatisfies,
  lensPath,
  equals,
  reject,
  __,
  mergeLeft,
  filter,
  complement,
  isEmpty,
  length,
  flatten,
  allPass,
  anyPass
} from 'ramda'
import * as Yup from 'yup'
import { Formik, Form } from 'formik'
import { noop } from 'lodash'
import { Button } from '@/components/button'
import { select } from '@/store'
import { colors } from '@/styles'
import { createModal, useModalize } from '@/components/modalize/mui'
import Tooltip from '@/components/popper-tooltip'
import useMediaQuery from '@/hooks/use-media-query'
import { fontWeights, breakpoints } from '@/styles'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'
import { defaultIfFalsy } from '@/utils'
import BasicInfoInputs from './basic-info-inputs'
import VoidModalContent from './void-modal-content'
import getUniqueColor from '../get-unique-color'
import { getFormattedSigners, getFormattedPackageFiles, getPrimaryLandlord } from '../utils'
import { showDocumentModal } from '../modal'

const Wrapper = styled.div`
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex: 1;
  width: 100%;
`

const ModalText = styled.p`
  font-weight: ${fontWeights.medium};
  font-size: 18px;
  line-height: 24px;
  margin: 0;
`

const FormWrapper = styled(Form)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: ${props => (props.editing ? 'space-between' : 'flex-end')};

  button:first-child {
    margin-right: 16px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    button {
      width: 50%;
      margin: 0;
    }
  }
`
const FullText = styled.div`
  display: flex;
  font-size: 16px;
  font-weight: ${fontWeights.medium};
  margin-bottom: 8px;

  img {
    margin-right: 10px;
  }
`

const TooltipWrapper = styled.div`
  p {
    max-width: 300px;
    margin: 0;
    padding: 0;
  }
`

const StyledTooltip = styled(Tooltip)`
  > button {
    width: 100%;
  }
`

const AddRecipientFormModal = ({
  contractId,
  user = {},
  ModalTitle,
  ModalFooter,
  editing = false,
  signees = {},
  setSignees = noop,
  fileMetadata = {},
  setFileMetadata = noop,
  currentUser = {},
  setUser = noop,
  items = {}
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const {
    packageFiles = [],
    signers = [],
    recipients = {}
  } = useSelector(state => select.contract.contractPackageInfo(state, { contractId }))

  const tenancyAgreementId = pipe(
    find(propEq('document_type', 'tenancy_agreement')),
    prop('id')
  )(packageFiles)

  const sentDocuments = pipe(filter(propEq('state_machine', 'sent')))(packageFiles)
  const hasSentDocument = pipe(complement(isEmpty))(sentDocuments)

  const hasItems = pipe(
    rValues,
    flatten,
    filter(
      allPass([
        propEq('recipient_id', user.recipient_id),
        anyPass([propEq('inputType', 'signature'), propEq('inputType', 'initials')])
      ])
    ),
    complement(isEmpty)
  )(items)

  const tooltipText = hasSentDocument
    ? t(
        'l.manage_recipients.has_sent_document.delete.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, they cannot be removed. Please void the contract and resend to modify.'
      )
    : t(
        'l.contract_manager.no_sent_document.delete.tooltip.text',
        'Since this recipient has at least one signature or initial field added to a document, they cannot be removed. Please delete these fields to modify.'
      )

  const userColor =
    pipe(
      pathOr([], ['contract_documents']),
      find(propEq('listing_contract_package_file_id', Number(tenancyAgreementId))),
      path(['metadata', 'color'])
    )(user) ?? getUniqueColor(recipients, packageFiles)

  const getSignerId = packageFileId =>
    pipe(
      pathOr([], ['contract_documents']),
      find(propEq('listing_contract_package_file_id', Number(packageFileId))),
      prop('listing_contract_package_signer_id')
    )(user)

  const getContractdocuments = values => {
    const contractDocuments = packageFiles.map(doc => {
      return {
        id: doc.id,
        listing_contract_package_file_id: doc.id,
        listing_contract_package_signer_id: getSignerId(doc.id),
        recipient_signing_status: values?.recipient_signing_status,
        metadata: {
          color: userColor
        }
      }
    })

    return contractDocuments
  }
  const handleTenancyAgreementDelete = async fileId => {
    const { response } = await dispatch.contract.removeTenancyAgreement({
      contractId,
      fileId
    })
    return response?.ok
  }

  const handlePackageFileDelete = async fileId => {
    const { response } = await dispatch.contract.removePackageFile({
      contractId,
      fileId
    })
    return response?.ok
  }

  const handleConfirm = async values => {
    const pageRequests = pipe(
      map(async value => {
        const voidFunction =
          value?.document_type === 'tenancy_agreement'
            ? handleTenancyAgreementDelete
            : handlePackageFileDelete
        await voidFunction(value?.id)
      })
    )(sentDocuments)
    const results = await Promise.all(pageRequests)
    if (filter(Boolean, results)) {
      await handleAddRecipients(values)
    }
  }

  const handleOnDelete = async () => {
    setIsLoading(true)
    await dispatch.contract.removeSignerWithDocuments({
      signerId: user.recipient_id,
      contractId
    })

    // setFileMetadata to remove required signers from the documents
    if (fileMetadata && setFileMetadata) {
      const updatedMetaData = pipe(
        pathOr([], ['signer']),
        map(reject(propSatisfies(equals(user.recipient_id), 'recipient_id'))),
        set(lensPath(['signer']), __, fileMetadata)
      )(fileMetadata)
      setFileMetadata(updatedMetaData)
    }

    // setSignees to update the signee list
    if (signees && setSignees)
      pipe(
        map(reject(propSatisfies(equals(user.recipient_id), 'recipient_id'))),
        setSignees
      )(signees)

    // Update current user for item entry
    if (currentUser && setUser) {
      const primaryLandlord = getPrimaryLandlord(recipients, signees)
      pipe(set(lensPath(['props']), primaryLandlord), setUser)(currentUser)
    }
    setIsLoading(false)
    close()
  }

  const handleOnSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true)
    if (hasSentDocument && values.recipient_signing_status === 'signature_required') {
      const confirmCta =
        length(sentDocuments) > 1
          ? t('l.contract_edit.void_all.cta', 'Void all')
          : t('l.contract_edit.void.cta', 'Void')

      showDocumentModal({
        title: t('l.contract_edit.are_you_sure.title', 'Are you sure?'),
        content: (
          <VoidModalContent
            first_name={values.first_name}
            last_name={values.last_name}
            sentDocuments={sentDocuments}
            isAdd
          />
        ),
        confirmCta: confirmCta,
        cancelCta: t('common.cancel', 'Cancel'),
        backgroundColor: colors.red96,
        confirmTheme: 'delete',
        confirmCtaIcon: 'void',
        cancelFn: noop,
        confirmFn: () => handleConfirm(values)
      })
    } else {
      handleAddRecipients(values)
    }
    setSubmitting()
  }

  const handleAddRecipients = async values => {
    const signer = {
      email: values.email,
      phone_number: values.phone_number,
      first_name: values.first_name,
      last_name: values.last_name,
      dob: values.dob,
      contract_documents: getContractdocuments(values)
    }

    await dispatch.contract.addRecipient({
      signer,
      contractId
    })

    const response = await dispatch.contract.getPackage({ contractId: contractId })
    const formattedSigners = getFormattedSigners(response?.body?.signers)
    const formattedFiles = getFormattedPackageFiles(response?.body?.package_files)

    setSignees(formattedSigners)
    setFileMetadata({
      ...fileMetadata,
      package_files: formattedFiles,
      signer: response?.body?.signers
    })

    close()
  }
  const initialValues = pipe(
    pickAll(['id', 'first_name', 'last_name', 'email', 'phone_number', 'dob']),
    evolve({
      id: defaultIfFalsy(''),
      first_name: defaultIfFalsy(''),
      last_name: defaultIfFalsy(''),
      email: defaultIfFalsy(''),
      dob: defaultIfFalsy(null)
    }),
    mergeLeft({
      recipient_signing_status: pathOr(
        'signature_required',
        ['contract_documents', 0, 'recipient_signing_status'],
        user
      )
    })
  )(user)

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .matches(/^[^0-9]*$/, t('b.profile.name.numbers.error', 'Cannot include numbers'))
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    last_name: Yup.string()
      .matches(/^[^0-9]*$/, t('b.profile.name.numbers.error', 'Cannot include numbers'))
      .matches(
        /^[ A-Za-zÀ-Ÿ-_]*$/,
        t('b.profile.name.special_characters.error', 'Cannot include special characters')
      )
      .required(),
    email: Yup.string().email().required(),
    phone_number: Yup.string().phone().required(),
    dob: Yup.string(),
    recipient_signing_status: Yup.string().required()
  })

  return (
    <Context.Provider
      value={{
        ModalTitle,
        ModalFooter,
        close
      }}
    >
      <ModalBase
        overflow="scroll"
        minHeight={295}
        title={
          editing
            ? t('l.contract.create.edit_recipient.title', 'Edit Recipient')
            : t('l.contract.create.manage_documents.title_add', 'Add New Recipient')
        }
      >
        <Wrapper>
          <Formik
            validationSchema={validationSchema}
            initialValues={initialValues}
            onSubmit={handleOnSubmit}
          >
            {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
              return (
                <>
                  <FormWrapper>
                    <ModalText>{t('l.contract.create.basic_info.title', 'Basic Info')}</ModalText>
                    <BasicInfoInputs
                      values={values}
                      setFieldValue={setFieldValue}
                      packageFiles={packageFiles}
                      signers={signers}
                      editing={editing}
                    />
                  </FormWrapper>
                  <ModalFooter>
                    <ButtonsWrapper editing={editing}>
                      {editing &&
                        (hasItems ? (
                          <StyledTooltip
                            enabled={true}
                            size="medium"
                            content={
                              <TooltipWrapper>
                                <FullText>{tooltipText}</FullText>
                              </TooltipWrapper>
                            }
                            placement={'top'}
                          >
                            <Button
                              type="button"
                              theme="delete"
                              onClick={handleOnDelete}
                              disabled={true}
                              size={displayMobile ? 'medium' : 'small'}
                            >
                              {t('common.remove', 'Remove')}
                            </Button>
                          </StyledTooltip>
                        ) : (
                          <Button
                            type="button"
                            theme="delete"
                            onClick={handleOnDelete}
                            disabled={isLoading || isSubmitting}
                            size={displayMobile ? 'medium' : 'small'}
                          >
                            {t('common.remove', 'Remove')}
                          </Button>
                        ))}
                      <Button
                        type="button"
                        theme="primary"
                        onClick={handleSubmit}
                        disabled={isLoading || isSubmitting}
                        size={displayMobile ? 'medium' : 'small'}
                      >
                        {t('common.save', 'Save')}
                      </Button>
                    </ButtonsWrapper>
                  </ModalFooter>
                </>
              )
            }}
          </Formik>
        </Wrapper>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(AddRecipientFormModal, {
  width: '640px',
  sheet: true,
  overflow: 'visible',
  showFooterOverflow: true
})
